import { cx } from 'lib/cx'
import { ensureNonEmptyChildren } from 'lib/slate'
import { useEffect, useRef } from 'react'
import { Editable as SlateEditable, useSlateStatic } from 'slate-react'
import { type EditableProps } from 'slate-react/dist/components/editable'
import type { EditorVariants } from '.'
import { useElementRenderer } from './Element'
import { handleTabKey } from './extensions/nestedLists'
import { useSetKeyEvent } from './KeyEventContext'
import { useLeafRenderer } from './Leaf'
import styles from './styles.module.css'
import { useDecorations } from './useDecorations'
import { useFocus } from './useFocus'

export function Editable({
  placeholder = 'Text eingeben',
  onKeyDown,
  variant,
  renderPlaceholderInReadOnly = false,
  autoFocus,
  ...props
}: EditableProps & {
  variant?: EditorVariants
  renderPlaceholderInReadOnly?: boolean
}) {
  const editor = useSlateStatic()
  const { useInitializer, children } = editor
  const { decorators } = useInitializer({ decorators: [] })
  const focus = useFocus()
  const shouldFocus = useRef(autoFocus && !props.readOnly)

  useEffect(() => {
    if (shouldFocus.current) focus()
  }, [focus])

  children.forEach(ensureNonEmptyChildren)

  const decorate = useDecorations(decorators)
  const renderElement = useElementRenderer()
  const renderLeaf = useLeafRenderer()
  const setKeyEvent = useSetKeyEvent()

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      handleTabKey(editor, event)
    }
    if (event.key === 'Escape') {
      event.currentTarget.blur()
    }
    setKeyEvent(event)
    onKeyDown?.(event)
  }

  return (
    <SlateEditable
      {...props}
      className={cx(
        'group',
        styles.editable,
        styles[variant ?? 'default'],
        props.className,
      )}
      decorate={decorate}
      translate={props.readOnly ? 'yes' : 'no'}
      onKeyDown={handleKeyDown}
      placeholder={
        !props.readOnly || renderPlaceholderInReadOnly ? placeholder : undefined
      }
      renderElement={renderElement}
      renderLeaf={renderLeaf}
      renderPlaceholder={({ children, attributes }) => (
        <span
          {...attributes}
          className={styles.placeholder}
          style={props.style ? { top: props.style?.paddingTop } : undefined}
        >
          {children}
        </span>
      )}
      spellCheck
    />
  )
}
